/* ==========================================================================
   Metrics Page
   Task F37: Operational metrics dashboard for internal team
   ========================================================================== */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { MetricsOverview } from '@/components/internal/MetricsOverview'
import { AtRiskTable } from '@/components/internal/AtRiskTable'
import { ChurnMetrics } from '@/components/internal/ChurnMetrics'
import {
  getOverviewMetrics,
  getAtRiskPharmacies,
  getChurnMetrics,
  type OverviewMetrics,
  type AtRiskListResponse,
  type ChurnMetrics as ChurnMetricsType,
} from '@/api/internal'
import { ApiClientError } from '@/api/client'

export function Metrics() {
  const navigate = useNavigate()

  const [overview, setOverview] = useState<OverviewMetrics | null>(null)
  const [atRisk, setAtRisk] = useState<AtRiskListResponse | null>(null)
  const [churn, setChurn] = useState<ChurnMetricsType | null>(null)

  const [loadingOverview, setLoadingOverview] = useState(true)
  const [loadingAtRisk, setLoadingAtRisk] = useState(true)
  const [loadingChurn, setLoadingChurn] = useState(true)

  const [error, setError] = useState<string>()

  const fetchAll = useCallback(async () => {
    setError(undefined)
    setLoadingOverview(true)
    setLoadingAtRisk(true)
    setLoadingChurn(true)

    try {
      const [overviewData, atRiskData, churnData] = await Promise.all([
        getOverviewMetrics(),
        getAtRiskPharmacies(),
        getChurnMetrics(),
      ])
      setOverview(overviewData)
      setAtRisk(atRiskData)
      setChurn(churnData)
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          navigate('/internal/login', { replace: true })
          return
        }
        setError(err.getUserMessage())
      } else {
        setError('Failed to load metrics.')
      }
    } finally {
      setLoadingOverview(false)
      setLoadingAtRisk(false)
      setLoadingChurn(false)
    }
  }, [navigate])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const isLoading = loadingOverview || loadingAtRisk || loadingChurn

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-deep-purple">Metrics</h1>
          <p className="text-sm text-charcoal/60 mt-1">
            Operational health and churn analytics
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={fetchAll}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Overview */}
      <section>
        <h2 className="text-lg font-semibold text-charcoal mb-3">Overview</h2>
        <MetricsOverview data={overview} loading={loadingOverview} />
      </section>

      {/* At-Risk */}
      <section>
        <AtRiskTable data={atRisk} loading={loadingAtRisk} />
      </section>

      {/* Churn */}
      <section>
        <h2 className="text-lg font-semibold text-charcoal mb-3">
          Churn & Revenue
        </h2>
        <ChurnMetrics data={churn} loading={loadingChurn} />
      </section>
    </div>
  )
}
